import {
  createApiRef,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/frontend-plugin-api';

/** Mirrors AnalyticsStats in analytics-backend (services/EventStore.ts). */
export interface AnalyticsStats {
  since: string;
  totals: { events: number; users: number | null };
  pages: Array<{ path: string; views: number; users: number }>;
  weeklyActiveUsers: Array<{ week: string; users: number }>;
  actions: Array<{
    pluginId: string | null;
    action: string;
    count: number;
    users: number;
  }>;
  searchesWithoutResults: Array<{ term: string; count: number }>;
}

export class AnalyticsStatsError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

export interface AnalyticsStatsApi {
  getStats(days: number): Promise<AnalyticsStats>;
}

export const analyticsStatsApiRef = createApiRef<AnalyticsStatsApi>({
  id: 'plugin.analytics.stats',
});

export class AnalyticsStatsClient implements AnalyticsStatsApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  async getStats(days: number): Promise<AnalyticsStats> {
    const baseUrl = await this.discoveryApi.getBaseUrl('analytics');
    const res = await this.fetchApi.fetch(`${baseUrl}/stats?days=${days}`);
    if (!res.ok) {
      const text = await res.text().catch(() => res.statusText);
      throw new AnalyticsStatsError(
        `Analytics API error ${res.status}: ${text}`,
        res.status,
      );
    }
    return res.json();
  }
}
